/**
 * seo1-bot W1 (2026-10-17) — 봇 UA 엣지 레이트리밋 판정 + 계측 스크립트 미로딩 판정. 순수 문자열 모듈(Edge 안전).
 *
 * 배경(10/17 오전 실측): ClaudeBot 98k hit, 그중 /search 33k. /search 는 noindex,follow 검색 결과라 색인 대상이 아니고
 *   매 요청 DB 검색을 돈다(force-dynamic) — 크롤 부하의 본체다.
 *
 * 규칙(지시 W1):
 *   ① /search(하위·쿼리 포함)는 «모든» 봇 UA 를 계열별 분당 SEARCH_PER_MIN 으로 자른다(구글·네이버 포함).
 *      ⛔ robots.txt 로는 막지 않는다(세션 A instr/cl/seo1_b) — 크롤러가 noindex 를 읽어야 기존 색인분이 빠진다.
 *      예외(search-bot 2026-10-23): 색인이 없는 AI 학습 수집 봇 그룹(GPTBot·ClaudeBot·anthropic-ai·Claude-Web·Reflectionbot)만 Disallow: /search.
 *      /search 는 메타 noindex,follow + X-Robots-Tag(next.config, live3-a) + 이 레이트리밋으로만 다룬다.
 *   ② 그 밖의 경로: 검색엔진(구글·네이버·빙·다음·줌·애플)은 «제한 없음». AI·SEO 수집 봇만 계열별 분당 CRAWL_PER_MIN.
 *   ③ 사용자가 직접 부른 실시간 조회 UA(ChatGPT-User·Claude-User·Perplexity-User)는 ② 에서 뺀다 — 답변 인용 경로다.
 *   ④ 사람 UA 는 여기 걸리지 않는다. «명시 목록» 만 본다 — /bot/ 같은 넓은 패턴을 쓰지 않는다
 *      (예: 휴대폰 모델명 「CUBOT」이 /bot/i 에 걸린다). 목록에 없는 봇은 통과(보수적 기본값).
 *
 * ⛔ 응답 본문을 UA 로 바꾸지 않는다(no-ua-cloaking.test.ts). 여기서 정하는 것은 «429 로 끊을지» 뿐이다.
 */

export type BotKind = 'search' | 'ai' | 'ai-user' | 'seo';
export type BotClass = { family: string; kind: BotKind };

/** 계열 이름 → [kind, 패턴]. 순서가 판정 순서다(구체 패턴 먼저). */
const FAMILIES: ReadonlyArray<readonly [string, BotKind, RegExp]> = [
  // 검색엔진 — /search 외에는 제한하지 않는다.
  ['google', 'search', /googlebot|google-inspectiontool|googleother|storebot-google|adsbot-google|mediapartners-google/i],
  ['naver', 'search', /\byeti\b|naverbot|naver-searchadvisor/i],
  ['bing', 'search', /bingbot|bingpreview|msnbot/i],
  ['daum', 'search', /daumoa|daumcrawler/i],
  ['zum', 'search', /zumbot/i],
  ['apple', 'search', /applebot(?!-extended)/i],
  // 사용자 실시간 조회 — /search 외에는 제한하지 않는다.
  ['chatgpt-user', 'ai-user', /chatgpt-user/i],
  ['claude-user', 'ai-user', /claude-user/i],
  ['perplexity-user', 'ai-user', /perplexity-user/i],
  // AI 수집·검색 색인 봇.
  ['claude', 'ai', /claudebot|claude-searchbot|claude-web|anthropic-ai/i],
  ['openai', 'ai', /gptbot|oai-searchbot/i],
  ['perplexity', 'ai', /perplexitybot/i],
  ['amazon', 'ai', /amazonbot/i],
  ['meta', 'ai', /meta-externalagent|meta-externalfetcher|facebookbot/i],
  ['bytedance', 'ai', /bytespider/i],
  ['commoncrawl', 'ai', /ccbot/i],
  ['cohere', 'ai', /cohere-ai|cohere-training/i],
  ['diffbot', 'ai', /diffbot/i],
  // search-bot (2026-10-23) — reflection.ai/bot. 10/22~23 밤 /search?q= 를 돌던 두 계열 중 하나(다른 하나 ClaudeBot).
  ['reflection', 'ai', /reflectionbot|reflection\.ai\/bot/i],
  ['petal', 'ai', /petalbot/i],
  ['yandex', 'ai', /yandexbot/i],
  ['baidu', 'ai', /baiduspider/i],
  // SEO 수집기.
  ['ahrefs', 'seo', /ahrefsbot/i],
  ['semrush', 'seo', /semrushbot/i],
  ['mj12', 'seo', /mj12bot/i],
  ['dotbot', 'seo', /dotbot/i],
  ['dataforseo', 'seo', /dataforseobot/i],
  ['barkrowler', 'seo', /barkrowler/i],
  ['serpstat', 'seo', /serpstatbot/i],
];

/** UA → 봇 계열. 목록에 없으면 null(사람 또는 미지 봇 — 둘 다 통과). */
export function classifyBotUa(ua: string | null | undefined): BotClass | null {
  if (!ua) return null;
  for (const [family, kind, re] of FAMILIES) if (re.test(ua)) return { family, kind };
  return null;
}

/** /search 는 봇 계열별 분당 이 수까지(noindex 를 읽을 만큼은 열어 두고 대량 수집만 자른다). */
export const SEARCH_PER_MIN = 6;
/** AI·SEO 수집 봇은 계열별(IP 무관) 분당 이 수까지 — 하루 상한 ≈ 86k. */
export const CRAWL_PER_MIN = 60;

export function isSearchResultPath(pathname: string): boolean {
  return pathname === '/search' || pathname.startsWith('/search/');
}

/**
 * 이 요청을 셀 레이트리밋 규칙. null = 세지 않는다(사람·검색엔진 일반 경로·사용자 조회 UA·GET/HEAD 외).
 * key 는 Upstash 키 접미(계열 단위 — IP 를 바꿔 가며 긁는 봇도 같은 통에 들어간다).
 */
export function botRateRule(
  pathname: string,
  method: string,
  ua: string | null | undefined,
): { key: string; limit: number; family: string; kind: BotKind } | null {
  if (method !== 'GET' && method !== 'HEAD') return null;
  const c = classifyBotUa(ua);
  if (!c) return null;
  if (isSearchResultPath(pathname)) return { key: `search:${c.family}`, limit: SEARCH_PER_MIN, ...c };
  if (c.kind === 'search' || c.kind === 'ai-user') return null;
  if (pathname.startsWith('/api/')) return null; // API 는 기존 IP 레이트리밋(kd_rl) 이 맡는다.
  return { key: `crawl:${c.family}`, limit: CRAWL_PER_MIN, ...c };
}

/**
 * 계측 스크립트(Vercel Analytics·Speed Insights) 를 싣지 않을 UA — 봇 목록 전부 + 헤드리스·자동화 브라우저.
 * 클라이언트에서 navigator.userAgent 로만 부른다(SSR HTML 은 UA 와 무관하게 같다).
 */
export function skipInsightsForUa(ua: string | null | undefined): boolean {
  if (!ua) return false;
  if (classifyBotUa(ua)) return true;
  return /headlesschrome|playwright|puppeteer|phantomjs|selenium|webdriver|lighthouse|chrome-lighthouse|pagespeed|gtmetrix/i.test(ua);
}
